import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/inter";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "./styles/app.css";
import { App } from "./App";
import { ApiProvider } from "./api/context";
import { createHttpApi } from "./api/http";
import type { CheckoutApi } from "./api/types";
import { BrandProvider } from "./branding/BrandContext";
import { DEFAULT_CONFIG } from "./branding/config";
import { CONFIG_FRESH_MS, fetchConfig, persistConfig, readCachedConfig } from "./branding/load";
import { ThemeProvider } from "./components/ThemeContext";
import { config } from "./config";
import { I18nProvider } from "./i18n";
import { PayBuildersContext, unwiredPayBuilders, type PayBuilders } from "./wallet/builders";
import { createPayBuilders } from "./wallet/realBuilders";

/** How long a first visit waits for /public/config before rendering with defaults. */
const FIRST_CONFIG_WAIT_MS = 1500;

async function boot() {
  let api: CheckoutApi;
  // DEV is a build-time constant: in a production build this branch and the mock module are gone.
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK === "1") {
    const { createMockApi } = await import("./mock/mockApi");
    api = createMockApi();
    console.info("[checkout] VITE_MOCK=1 — using the in-memory mock API (local development only).");
  } else {
    api = createHttpApi(config.apiUrl);
  }

  // Branding: a cached config paints immediately and is revalidated in the background; a first
  // visit waits briefly for the server and otherwise renders with the documented defaults.
  const cached = readCachedConfig();
  let initial = cached?.config ?? null;
  let revalidate = cached !== null && Date.now() - cached.savedAt > CONFIG_FRESH_MS;
  if (!initial) {
    const fetched = await fetchConfig(api, FIRST_CONFIG_WAIT_MS);
    if (fetched) persistConfig(fetched);
    initial = fetched ?? DEFAULT_CONFIG;
    // Unreachable or slow: try once more in the background instead of blocking the page.
    revalidate = fetched === null;
  }

  // Mock mode keeps its own simulated wallet flow; real builders load their chain code lazily.
  const builders: PayBuilders = config.mock ? unwiredPayBuilders : createPayBuilders(config.baseRouterAddress);

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrandProvider api={api} initial={initial} revalidate={revalidate}>
        <I18nProvider>
          <ThemeProvider>
            <ApiProvider api={api}>
              <PayBuildersContext.Provider value={builders}>
                <App />
              </PayBuildersContext.Provider>
            </ApiProvider>
          </ThemeProvider>
        </I18nProvider>
      </BrandProvider>
    </StrictMode>,
  );
}

void boot();
